/**
 * Practice Home — rep's landing page for the Practice section.
 * Shows: today's drill, own rank, CTAs into Role Play + Leaderboard.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Sparkles, Trophy, Zap, Play, Flame, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PracticeHome() {
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [drill, setDrill] = useState(null);
    const [rank, setRank] = useState(null);
    const [recentSessions, setRecentSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [d, lb, rs] = await Promise.all([
                    axios.get(`${API}/micro-practice/today`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
                    axios.get(`${API}/role-play/leaderboard`, { headers: getAuthHeader(), params: { days: 30 } }).catch(() => ({ data: { leaderboard: [] } })),
                    axios.get(`${API}/role-play/sessions`, { headers: getAuthHeader(), params: { limit: 3 } }).catch(() => ({ data: { sessions: [] } })),
                ]);
                setDrill(d.data);
                const you = (lb.data?.leaderboard || []).find(r => r.is_you);
                setRank(you);
                setRecentSessions(rs.data?.sessions || []);
            } finally {
                setLoading(false);
            }
        })();
    }, [getAuthHeader]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Practice
            </h1>
            <p className="text-sm text-stone-500 mb-6">
                Rehearse calls with AI avatars, drill daily, and climb the team leaderboard.
            </p>

            {/* Two primary CTAs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                <button
                    onClick={() => navigate('/practice/role-play/new')}
                    className="flex items-center gap-4 p-5 rounded-2xl text-white text-left overflow-hidden relative hover:brightness-110 transition-all"
                    style={{ background: 'linear-gradient(135deg, #2d1a6e, #1a0f3d)' }}
                >
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.12)', color: '#D4C7FF' }}
                    >
                        <Sparkles className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-lg font-bold flex items-center gap-2">
                            Role Play
                            <span
                                className="text-[9px] font-bold px-1.5 py-0.5 rounded tracking-widest"
                                style={{ background: 'rgba(255,255,255,0.15)', color: '#D4C7FF' }}
                            >AI</span>
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>
                            Rehearse with AI avatars → 6-pillar debrief
                        </p>
                    </div>
                </button>

                <button
                    onClick={() => navigate('/micro-practice')}
                    className="flex items-center gap-4 p-5 rounded-2xl text-left border hover:shadow-md transition-all"
                    style={{
                        background: 'linear-gradient(135deg, #F4F0FF, #FFFFFF)',
                        borderColor: '#D4C7FF',
                    }}
                >
                    <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white"
                        style={{ color: '#5D3FD3', border: '1px solid #D4C7FF' }}
                    >
                        <Zap className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-lg font-bold">Today's 5-min drill</div>
                        <p className="text-xs text-stone-600 mt-0.5 truncate">
                            {drill?.topic || 'Personalised from your last calls'}
                        </p>
                    </div>
                    {drill?.streak_days > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-700 flex-shrink-0">
                            <Flame className="w-3.5 h-3.5" />
                            <span className="text-xs font-bold">{drill.streak_days}</span>
                        </div>
                    )}
                </button>
            </div>

            {/* Rank */}
            {rank ? (
                <button
                    onClick={() => navigate('/leaderboard')}
                    className="w-full mb-6 flex items-center gap-4 p-4 bg-white border border-stone-200 rounded-xl hover:border-stone-300 hover:shadow-sm transition-all text-left"
                >
                    <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #5D3FD3, #8E7AF5)' }}
                    >
                        <Trophy className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-bold">
                            You're <span className="text-violet-600">#{rank.rank}</span> on the team leaderboard
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">
                            {rank.avg_score} avg · {rank.sessions_count} sessions · 🔥 {rank.streak_days} day streak
                        </div>
                    </div>
                    <span className="text-xs text-stone-400">See leaderboard →</span>
                </button>
            ) : (
                <button
                    onClick={() => navigate('/leaderboard')}
                    className="w-full mb-6 flex items-center gap-4 p-4 bg-white border border-stone-200 rounded-xl hover:border-stone-300 transition-all text-left"
                >
                    <Trophy className="w-5 h-5 text-stone-400 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-stone-700">Team leaderboard</div>
                        <div className="text-xs text-stone-500">Run your first Role Play to appear on the board.</div>
                    </div>
                    <span className="text-xs text-stone-400">Open →</span>
                </button>
            )}

            {/* Recent role plays */}
            {recentSessions.length > 0 && (
                <>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xs font-bold tracking-widest uppercase text-stone-500">
                            Recent sessions
                        </h2>
                        <button
                            onClick={() => navigate('/practice/role-play')}
                            className="text-xs font-semibold text-violet-600 hover:text-violet-700"
                        >
                            See all →
                        </button>
                    </div>
                    <div className="space-y-2">
                        {recentSessions.map(s => (
                            <button
                                key={s.session_id}
                                onClick={() => navigate(`/practice/role-play/sessions/${s.session_id}`)}
                                className="w-full text-left flex items-center gap-3 p-3 bg-white border border-stone-200 rounded-xl hover:border-stone-300 transition-all"
                            >
                                <div className="flex-1">
                                    <div className="text-sm font-semibold flex items-center gap-2">
                                        <Play className="w-3.5 h-3.5 text-stone-400" />
                                        Role Play · {s.difficulty}
                                    </div>
                                    <div className="text-xs text-stone-500 mt-0.5">
                                        {new Date(s.started_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-bold">
                                        {s.overall_score == null ? '—' : s.overall_score}
                                    </div>
                                    <div className="text-[10px] text-stone-400 uppercase font-bold tracking-widest">
                                        Score
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
